import { html } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { ZoomIn, ZoomOut } from 'lucide';
import { lucideIcon } from '../icon.js';

/**
 * De boorprofielen van een labopdracht: de werkbalk (legenda, Y-as, zoom) en de kolommen met
 * de Y-as ernaast. Gedeeld door de labopdracht zelf (lab-mengmonsters) en het aparte
 * profielvenster (lab-profielvenster), zodat ze er op beide schermen hetzelfde uitzien.
 *
 * Alles gaat als gewone data (het 'model'), zodat de labopdracht het ook over een
 * BroadcastChannel naar het venster kan sturen; wat je aanklikt komt via `acties` terug. Het
 * model maakt lab-mengmonsters (_profielModel); deze module rekent niets uit.
 *
 * Model van de werkbalk: { legendaOpen, legenda: [[fill, label]], asModus, procent, kleinste, grootste }
 * Model van de profielen: { kolommen, ticks, nap, top, zonderZ, kolomB, balkB, vergrendeld, leeg }
 *   kolom: { id, naam, meta, eind, lagen: [{ sleutel, van, tot, hoogte, gapBefore, fill, beschrijving,
 *            meta, chips: [{ naam, bg, rand, kleur }], past, geenMonster, potjes }] }
 *   potje: { id, naam, van, tot, geroerd, erin, bezetDoor, gedimd, tip: { tekst, rijen } }
 */

export const cmNaarM = (cm) => (cm / 100).toFixed(2).replace('.', ',');

/** Ongeroerd (steekbus) krijgt een icoontje op het potje: het lab behandelt die anders. */
const ICOON_STEEKBUS = html`<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor"
  stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="7" y="3" width="10" height="18" rx="2"></rect><path d="M7 8h10"></path></svg>`;

/** Legenda links; de Y-as (Maaiveld | NAP) en de zoom rechts, op één regel. */
export function renderProfielbalk(m, a) {
  return html`
    <div class="profielbalk">
      <button class="legendaknop" @click=${() => a.wisselLegenda()}>
        <svg class=${m.legendaOpen ? 'pijl' : 'pijl dicht'} width="11" height="11" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="m6 9 6 6 6-6"></path>
        </svg>Legenda
      </button>
      ${m.legendaOpen ? html`
        <div class="legenda">
          ${m.legenda.map(([fill, label]) => html`
            <span class="legitem">
              <svg width="12" height="12" aria-hidden="true">
                <rect x="0.5" y="0.5" width="11" height="11" rx="2" fill=${fill} stroke="#8a968d" stroke-width="0.8"></rect>
              </svg>${label}
            </span>`)}
        </div>` : ''}
      <!-- De Y-as: diepte onder maaiveld (elke boring begint bovenaan) of hoogte t.o.v. NAP
           (elke boring begint op zijn eigen maaiveld, zodat je hoogteverschillen ziet). -->
      <span class="asknoppen" role="group" aria-label="Y-as van de profielen">
        <button class=${m.asModus === 'maaiveld' ? 'aan' : ''} aria-pressed=${m.asModus === 'maaiveld'}
                title="Diepte onder maaiveld: elke boring begint bovenaan op 0 m"
                @click=${() => a.zetAs('maaiveld')}>Maaiveld</button>
        <button class=${m.asModus === 'nap' ? 'aan' : ''} aria-pressed=${m.asModus === 'nap'}
                title="Hoogte t.o.v. NAP: elke boring begint op zijn eigen maaiveldhoogte"
                @click=${() => a.zetAs('nap')}>NAP</button>
      </span>
      <span class="schaal" title="Zoom van de profielen">
        <button ?disabled=${m.kleinste} title="Uitzoomen" aria-label="Uitzoomen"
                @click=${() => a.zoom(-1)}>${unsafeSVG(lucideIcon(ZoomOut, 14))}</button>
        <span class="schaalprocent">${m.procent}%</span>
        <button ?disabled=${m.grootste} title="Inzoomen" aria-label="Inzoomen"
                @click=${() => a.zoom(1)}>${unsafeSVG(lucideIcon(ZoomIn, 14))}</button>
      </span>
    </div>`;
}

/**
 * De Y-as en de kolommen, voor in een `.vergelijk` (die de aanroeper maakt: daar hangen de
 * tooltip en de greep aan). Acties: klikPotje(kolomId, potId), wisselBoring(kolomId),
 * markeer(kolomId | null), bzbMeer(event, kolomNaam, laag).
 */
export function renderProfielKolommen(m, a) {
  return html`
    ${renderYas(m.ticks, m.nap, m.top)}
    ${m.kolommen.map((k) => renderKolom(k, m, a))}`;
}

/**
 * De Y-as links van de profielen, op dezelfde gedeelde as als de kolommen: in m onder maaiveld
 * of in m NAP. Labels die elkaar zouden raken vallen weg.
 */
function renderYas(ticks, nap, top) {
  let vorige = -Infinity;
  const zichtbaar = ticks.filter((t) => (t.y - vorige >= 14 ? ((vorige = t.y), true) : false));
  const label = (d) => (nap
    ? (top - d / 100).toFixed(2).replace('.', ',').replace(/^(?!-)/, '+')
    : cmNaarM(d));
  const hoogte = ticks.at(-1)?.y || 0;
  return html`
    <div class="yas">
      <div class="kolomkop"><span class="aseenheid">${nap ? 'm NAP' : 'm –mv'}</span></div>
      <div class="kolommeta">${nap ? 'hoogte' : 'diepte'}</div>
      <div class="asvak" style="height:${hoogte}px">
        ${zichtbaar.map((t) => html`<span class="astick" style="top:${t.y}px">${label(t.d)}</span>`)}
      </div>
    </div>`;
}

function renderKolom(k, m, a) {
  return html`
    <div class="kolom" @mouseenter=${() => a.markeer(k.id)} @mouseleave=${() => a.markeer(null)}>
      <div class="kolomkop">
        <strong title=${k.naam}>Boring ${k.naam}</strong>
        <span class="spacer"></span>
        <button class="kolomweg" title="Uit de vergelijking halen" @click=${() => a.wisselBoring(k.id)}>×</button>
      </div>
      <div class="kolommeta" title=${k.meta}>${k.meta}</div>

      ${k.lagen.map((l, i) => html`
        <!-- Boven de eerste laag (op NAP, bij een lager maaiveld) is het gat lucht: geen streep. -->
        ${l.gapBefore > 0 ? html`<div class="laaggat ${i === 0 ? 'boven' : ''}" style="height:${l.gapBefore}px"></div>` : ''}
        <div class="laagrij ${l.past ? '' : 'valtaf'}" style="height:${l.hoogte}px">
          <span class="grens"><span>${cmNaarM(l.van)}</span></span>
          <!-- De potjes liggen in het profiel zelf, op de laag waar ze uit komen: zo zie je
               zonder te zoeken welk potje bij welke grond hoort, en scheelt het een kolom. -->
          <!-- De monstercode ligt achter de textuur, en die is daar doorzichtig: zo lees je
               de code en zie je nog steeds welke grond het is. -->
          <div class="balk ${l.potjes.length ? 'bemonsterd' : ''}">
            <svg class="textuur" aria-hidden="true"><rect width="100%" height="100%" fill=${l.fill}></rect></svg>
            ${l.potjes.length ? html`
              <div class="balkdelen">
                ${l.potjes.map((p) => html`
                  <button class="potje ${p.erin ? 'erin' : ''} ${p.bezetDoor ? 'bezet' : ''} ${p.gedimd ? 'gedimd' : ''}"
                          ?disabled=${!!p.bezetDoor || m.vergrendeld}
                          data-tip=${p.tip.tekst}
                          data-tip-rows=${JSON.stringify(p.tip.rijen)}
                          @click=${() => a.klikPotje(k.id, p.id)}>
                    ${p.geroerd ? '' : ICOON_STEEKBUS}<span>${p.naam}</span>
                  </button>`)}
              </div>` : ''}
          </div>
          <!-- De rij heeft een vaste hoogte (de gedeelde diepte-as): wat niet past valt weg,
               en staat voluit in de tooltip. -->
          <div class="laagtekst" title=${[l.beschrijving, l.meta, ...l.chips.map((c) => c.naam),
            l.geenMonster ? 'niet bemonsterd' : ''].filter(Boolean).join('\n')}>
            <div class="laagnaam">${l.beschrijving}</div>
            ${l.meta ? html`<div class="laagextra">${l.meta}</div>` : ''}
            ${l.chips.length || l.geenMonster ? html`
              <div class="bzbrij">
                ${l.chips.map((c) => html`
                  <span class="bzb" style=${`background:${c.bg};border-color:${c.rand};color:${c.kleur}`}>${c.naam}</span>`)}
                <!-- Passen niet alle BZB's in de laag, dan verbergt pasBzbIn de laatste en
                     zegt deze knop hoeveel er nog zijn; een klik toont ze allemaal. -->
                ${l.chips.length ? html`
                  <button class="bzbmeer" hidden title="Alle bijzondere bestanddelen van deze laag"
                          @click=${(e) => a.bzbMeer(e, k.naam, l)}></button>` : ''}
                ${l.geenMonster ? html`<span class="geenmonster">niet bemonsterd</span>` : ''}
              </div>` : ''}
          </div>
        </div>`)}
      <div class="kolomvoet">
        <span class="grens"><span>${k.eind}</span></span>
        <span class="voetstreep"></span>
      </div>
    </div>`;
}

/**
 * Passen de BZB-labels niet in de hoogte van de laag (de gedeelde diepte-as bepaalt die), dan
 * verbergen we ze van achteren af tot het past, met een tegel '+N' erachter. Een klik op die
 * tegel toont ze allemaal (renderBzbKaart). Zo valt er nooit tekst onzichtbaar buiten de laag.
 *
 * Bij elke herteking en bij een andere maat opnieuw: is er ruimte (grotere schaal, breder
 * scherm), dan staan ze er gewoon allemaal. Past zelfs de tegel niet onder de naam, dan komt
 * hij op de regel van de naam (.krap).
 */
export function pasBzbIn(root) {
  for (const tekst of root.querySelectorAll('.laagtekst')) {
    const rij = tekst.querySelector('.bzbrij');
    const meer = rij?.querySelector('.bzbmeer');
    if (!meer) continue;
    const chips = [...rij.querySelectorAll('.bzb')];
    chips.forEach((c) => { c.hidden = false; });
    meer.hidden = true;
    tekst.classList.remove('krap', 'zonderextra');
    const past = () => tekst.scrollHeight <= tekst.clientHeight + 1;
    if (past()) continue;
    meer.hidden = false;
    let verborgen = 0;
    for (let i = chips.length - 1; i >= 0 && !past(); i--) {
      chips[i].hidden = true;
      verborgen += 1;
      meer.textContent = `+${verborgen}`;
    }
    meer.textContent = `+${verborgen}`;
    // Zelfs zonder labels past de rij niet: de tegel naar de regel van de laagnaam, en een
    // half afgesneden tweede regel (kleur, korrel, diepte) weg: die staat in het kaartje.
    if (!past()) tekst.classList.add('krap');
    if (!past()) tekst.classList.add('zonderextra');
  }
}

/** Waar het kaartje met alle BZB's van een laag komt: onder de '+N'-tegel, binnen het scherm. */
export function bzbKaartVan(e, boringNaam, laag) {
  e.stopPropagation();
  const r = e.currentTarget.getBoundingClientRect();
  return {
    boring: boringNaam, beschrijving: laag.beschrijving, meta: laag.meta, van: laag.van, tot: laag.tot, chips: laag.chips,
    geenMonster: !!laag.geenMonster,
    x: Math.max(8, Math.min(r.left, window.innerWidth - 336)),
    y: r.bottom + 6 + 260 > window.innerHeight ? Math.max(8, r.top - 266) : r.bottom + 6,
  };
}

export function renderBzbKaart(b, sluit) {
  if (!b) return '';
  return html`
    <div class="bzbachter" @click=${sluit}></div>
    <div class="bzbkaart" role="dialog" aria-label="Bijzondere bestanddelen" style=${`left:${b.x}px;top:${b.y}px`}>
      <div class="bzbkop">
        <div>
          <strong>${b.boring} · ${cmNaarM(b.van)}–${cmNaarM(b.tot)} m</strong>
          <span>${b.beschrijving}</span>
          ${b.meta ? html`<span>${b.meta}</span>` : ''}
          ${b.geenMonster ? html`<span>niet bemonsterd</span>` : ''}
        </div>
        <button class="kolomweg" title="Sluiten" @click=${sluit}>×</button>
      </div>
      <div class="bzbrij alle">
        ${b.chips.map((c) => html`
          <span class="bzb" style=${`background:${c.bg};border-color:${c.rand};color:${c.kleur}`}>${c.naam}</span>`)}
      </div>
    </div>`;
}

/**
 * De tooltip van een potje uitstellen tot 0,3 seconde: zonder die aanloop flitst hij mee
 * terwijl je over een rij potjes beweegt. Eenmaal in beeld volgt hij de cursor.
 */
export function maakTipVertraging(tipMove, tipHide) {
  let timer = null, doel = null, aan = false, punt = null;
  const weg = () => { clearTimeout(timer); timer = null; doel = null; aan = false; tipHide(); };
  const beweeg = (e) => {
    const el = e.target?.closest?.('[data-tip]');
    if (!el) { weg(); return; }
    punt = { target: el, clientX: e.clientX, clientY: e.clientY };
    if (aan) { tipMove(punt); return; }
    if (doel === el) return;
    clearTimeout(timer);
    doel = el;
    timer = setTimeout(() => { aan = true; tipMove(punt); }, 300);
  };
  return { beweeg, weg };
}

export const PROFIELEN_CSS = `
    .profielbalk { display:flex; align-items:center; gap:10px 14px; margin-bottom:12px; }
    .profielbalk .legenda { flex:1; min-width:0; }
    .profielbalk .asknoppen { margin-left:auto; }
    .profielbalk .schaal { flex:none; }
    .leegvlak { padding:28px 16px; border:1.5px dashed var(--mv-border); border-radius:10px; text-align:center;
                font-size:.78rem; color:var(--mv-text-muted); }

    .legendaknop { display:inline-flex; align-items:center; gap:4px; border:none; background:none; padding:0;
                   font:inherit; font-size:.68rem; font-weight:800; color:var(--mv-text-muted); cursor:pointer; }
    .pijl { transition:transform .12s ease; }
    .pijl.dicht { transform:rotate(-90deg); }
    .legenda { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .legitem { display:inline-flex; align-items:center; gap:5px; font-size:.68rem; color:var(--mv-text-muted); }
    .schaal { display:inline-flex; align-items:center; gap:4px; font-size:.68rem; font-weight:700; color:var(--mv-text-muted); }
    .schaal button { display:inline-grid; place-items:center; width:22px; height:22px; padding:0; border:1.5px solid var(--mv-border); border-radius:6px;
      background:var(--mv-surface); color:var(--mv-text); font:inherit; font-size:.9rem; font-weight:800; line-height:1; cursor:pointer; }
    .schaal button:hover:not(:disabled) { border-color:var(--mv-primary); color:var(--mv-primary); }
    .schaal button:disabled { opacity:.4; cursor:default; }
    .schaalprocent { min-width:3.2em; text-align:center; font-variant-numeric:tabular-nums; }
    .asknoppen { display:inline-flex; flex:none; border:1.5px solid var(--mv-border); border-radius:8px; overflow:hidden; }
    .asknoppen button { padding:3px 10px; border:none; background:var(--mv-surface); font:inherit; font-size:.72rem; font-weight:800;
                        color:var(--mv-text-sub); cursor:pointer; }
    .asknoppen button + button { border-left:1.5px solid var(--mv-border); }
    .asknoppen button.aan { background:var(--mv-primary-tint); color:var(--mv-primary-hover); }

    /* ── De boorprofielen ───────────────────────────────────────────────── */
    .vergelijk { display:flex; gap:26px; overflow:auto; max-height:var(--profiel-hoogte, 78vh); padding:8px 2px 6px 0; }
    .kolom { flex:0 0 var(--kolom-b, 300px); min-width:0; }
    .kolomkop { display:flex; align-items:baseline; gap:8px; height:20px; }
    .kolomkop strong { font-size:.88rem; font-weight:900; color:var(--mv-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .kolom:hover .kolomkop strong { color:var(--mv-primary); }
    .kolomweg { border:none; background:none; padding:0 2px; font-size:1rem; line-height:1; color:var(--mv-text-muted); cursor:pointer; }
    .kolomweg:hover { color:var(--mv-danger); }
    /* Op één regel: de koppen moeten even hoog zijn, anders begint de diepte-as niet overal
       op dezelfde hoogte. De hele tekst staat in de tooltip. */
    .kolommeta { margin:2px 0 12px; font-size:.68rem; color:var(--mv-text-muted);
                 white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

    .laagrij { display:flex; gap:6px; transition:opacity .15s ease; }
    .laaggat { border-left:1px dashed #c3cbc4; margin-left:36px; }
    .laaggat.boven { border-left:none; }
    /* De Y-as: blijft links staan als je de profielen opzij schuift. */
    .yas { position:sticky; left:0; z-index:3; flex:0 0 52px; background:var(--mv-surface); margin-right:-14px; }
    .yas .kolommeta { color:var(--mv-text-muted); }
    .aseenheid { font-size:.7rem; font-weight:800; color:var(--mv-text-sub); white-space:nowrap; }
    .asvak { position:relative; border-right:1.5px solid var(--mv-text-sub); }
    .astick { position:absolute; right:6px; transform:translateY(-50%); font-size:.66rem; font-weight:700; color:var(--mv-text-sub);
              font-family:ui-monospace,SFMono-Regular,Menlo,monospace; white-space:nowrap; }
    .astick::after { content:''; position:absolute; right:-7px; top:50%; width:5px; border-top:1.5px solid var(--mv-text-sub); }
    .asnoot { margin:6px 0 0; font-size:.68rem; color:var(--mv-text-muted); }
    /* Voldoet niet aan het filter: blijft staan, maar treedt terug. */
    .laagrij.valtaf { opacity:.28; }
    .grens { width:30px; flex:none; position:relative; }
    .grens > span { position:absolute; right:0; top:-6px; font-size:.62rem; font-weight:700; color:var(--mv-text-muted);
                    font-family:ui-monospace,SFMono-Regular,Menlo,monospace; }
    /* Het profiel zelf: de textuur van de grondsoort, met de potjes erin op de laag waar ze uit
       komen. Breed genoeg voor een monsternaam als 'SLEUF1-01'. */
    .balk { width:var(--balk-b, 78px); flex:none; position:relative; border-left:1px solid #8a968d; border-right:1px solid #8a968d;
            border-top:1px solid #33422f; }
    /* De textuur ligt bovenop en laat klikken door naar de potjes eronder. Waar potjes zitten
       is hij doorzichtig, zodat de code erdoorheen leesbaar is; een laag zonder potje houdt
       zijn volle textuur. */
    .balk .textuur { position:absolute; inset:0; z-index:1; width:100%; height:100%; display:block; pointer-events:none; }
    .balk.bemonsterd .textuur { opacity:.4; }
    .balkdelen { position:absolute; inset:0; display:flex; flex-direction:column; }
    /* Een potje is een vak van de balk; een stippellijn waar het ene overgaat in het volgende. */
    .potje { flex:1 1 0px; min-height:19px; display:flex; align-items:center; justify-content:center; gap:3px; padding:0 4px;
             border:none; border-radius:0; background:#fff; color:#1b2a1e;
             font:inherit; font-size:.64rem; font-weight:900; white-space:nowrap; overflow:hidden; cursor:pointer; }
    .potje + .potje { border-top:1px dashed #33422f; }
    .potje span { overflow:hidden; text-overflow:ellipsis; }
    .potje svg { flex:none; }
    .potje:hover:not(:disabled) { background:var(--mv-primary-tint); color:#2f6122; }
    .potje.erin { background:var(--mv-primary); color:#fff; }
    /* Al in een ánder mengmonster: het potje blijft staan, maar is niet meer te pakken.
       Weglaten zou de indruk wekken dat het monster niet bestaat. */
    .potje.bezet { background:#e6e9e6; color:#8a968d; cursor:not-allowed; }
    .potje:disabled:not(.bezet) { cursor:default; }
    /* Andere verpakking dan het filter vraagt. */
    .potje.gedimd:not(.erin) { color:#a7b0a8; background:#f4f6f4; }
    .laagtekst { flex:1; min-width:0; padding:3px 0 0; border-top:1px solid var(--mv-border-soft); overflow:hidden; }
    /* Eén regel per onderdeel: een dunne laag toont dan zijn naam, een dikke ook de rest. */
    .laagnaam { font-size:.76rem; font-weight:700; line-height:1.3; color:var(--mv-text);
                white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .laagextra { font-size:.66rem; color:var(--mv-text-muted); margin-top:1px;
                 white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .bzbrij { display:flex; align-items:center; gap:4px; flex-wrap:wrap; margin-top:4px; }
    .bzb { font-size:.62rem; font-weight:800; padding:1px 6px; border-radius:5px; border:1px solid transparent; }
    .bzb[hidden], .bzbmeer[hidden] { display:none; }
    .bzbmeer { font:inherit; font-size:.62rem; font-weight:900; padding:1px 7px; border-radius:5px; cursor:pointer;
               border:1px solid var(--mv-text-sub); background:var(--mv-surface); color:var(--mv-text); }
    .bzbmeer:hover { background:#22371f; border-color:#22371f; color:#fff; }
    /* Te dun voor zelfs de tegel onder de naam: de tegel op de regel van de naam. */
    .laagtekst { position:relative; }
    .laagtekst.krap .laagnaam { padding-right:38px; }
    .laagtekst.krap .bzbmeer { position:absolute; top:3px; right:0; }
    .laagtekst.zonderextra .laagextra { display:none; }
    .laagtekst.krap .bzbrij { margin-top:0; }
    .laagtekst.zonderextra .geenmonster { display:none; }
    /* Het kaartje met alle BZB's van een laag. */
    .bzbachter { position:fixed; inset:0; z-index:40; }
    .bzbkaart { position:fixed; z-index:41; width:320px; max-height:260px; overflow:auto; box-sizing:border-box;
                padding:10px 12px 12px; border:1px solid var(--mv-border); border-radius:10px; background:var(--mv-surface);
                box-shadow:0 12px 32px rgba(16,28,18,.18); }
    .bzbkop { display:flex; align-items:flex-start; gap:8px; margin-bottom:8px; }
    .bzbkop > div { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
    .bzbkop strong { font-size:.78rem; color:var(--mv-text); }
    .bzbkop span { font-size:.7rem; color:var(--mv-text-muted); }
    .bzbrij.alle { margin-top:0; }
    .geenmonster { font-size:.62rem; font-weight:800; color:var(--mv-text-muted); }
    .kolomvoet { display:flex; gap:6px; }
    .voetstreep { width:calc(var(--balk-b, 78px) + 2px); flex:none; height:0; border-top:1px solid #33422f; }
`;

/** Kanaal tussen de labopdracht en het profielvenster; los van dat van het kaartvenster. */
export const profielKanaalNaam = (sleutel) => `mv-lab-profielen-${sleutel}`;
